import React, { useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import Box from 'ui-box';
import { CompanyThumbnail } from '../companies';
import {
  Button,
  Container,
  defaultColors,
  Icon,
  KeyValue,
  majorScale,
  Menu,
  MenuDivider,
  MenuItem,
  minorScale,
  PageContainer,
  Popover,
  Section,
  Table,
  Tag,
  PageLoader,
  Heading,
  defaultFills,
  useAlert,
  useToast,
} from '../../npaper';
import { PageHeader } from '../../npaper/PageHeader';
import { ProductDetailsDialog } from './ProductDetailsDialog';
import { SupplierProductDialog } from './SupplierProductDialog';
import {
  IBOMItem,
  ICompany,
  IProduct,
  IProductStockLocation,
  ISupplierItem,
  ITeamMember,
  ProductType,
  useStores,
} from '../../stores';
import { ContributionMarginText, CostPriceText } from './PriceTexts';
import { ProductThumbnail } from './ProductThumbnail';
import { observer } from 'mobx-react-lite';
import { Spinner } from '../../npaper/Spinner';
import { BOMDialog } from './BOMDialog';
import { MoveStockDialog, StockDialog } from './StockDialog';
import { FormattedUnit } from '../../npaper/FormattedUnit';
import { UserThumbnail } from '../settings/UserThumbnail';
import { UserSelect } from '../../npaper/UserSelect';
import { getSnapshot } from 'mobx-state-tree';
import {
  getProductTypeIconName,
  getProductTypeTitle,
} from './ProductTypeTitle';
import ProductStatistics from './statistics/ProductStatistics';

export const ProductPage = observer(() => {
  const match = useRouteMatch<{ id: string }>();
  const history = useHistory();
  const id = match.params.id;
  const intl = useIntl();
  const toaster = useToast();

  const [product, setProduct] = useState<IProduct>();
  const { productStore } = useStores();

  const [notFound, setNotFound] = useState(false);

  const deleteAlert = useAlert({
    intent: 'danger',
    icon: 'trash',
    title: intl.formatMessage({
      defaultMessage: 'Archive product',
      description: 'Archive product alert title',
    }),
    message: intl.formatMessage({
      defaultMessage:
        'Are you sure you want to archive this product? We will mark the product as archived but we will not delete it.',
      description: 'Archive product description',
    }),
    confirmButtonText: intl.formatMessage({
      defaultMessage: 'Archive',
      description: 'Archive product alert confirm button',
    }),
  });

  useEffect(() => {
    let isSub = true;
    setProduct(undefined);
    productStore
      .getItem(id, true)
      .then((prod) => isSub && setProduct(prod))
      .catch((err) => {
        setNotFound(true);
        throw err;
      });
    return () => {
      isSub = false;
    };
  }, [productStore, id]);

  const [showDetails, setShowDetails] = useState(false);
  if (!product) {
    return (
      <PageContainer>
        {notFound ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Icon
              icon="error"
              size={40}
              marginBottom={minorScale(2)}
              color={defaultFills.danger[500]}
            />
            <Heading size={400}>
              <FormattedMessage defaultMessage="Could not find product" />
            </Heading>
            <p className="muted font-mono">ID {id}</p>
          </Box>
        ) : (
          <PageLoader />
        )}
      </PageContainer>
    );
  }
  return (
    <PageContainer>
      <ProductDetailsDialog
        isShown={showDetails}
        onClose={() => setShowDetails(false)}
        product={product}
      />

      <Container paddingBottom={majorScale(10)}>
        <PageHeader
          title={
            product.deleted_at ? (
              <>
                <Heading
                  size={600}
                  style={{
                    textDecoration: 'line-through',
                  }}
                >
                  {product.name}
                </Heading>
                <Tag intent="warning" small>
                  <FormattedMessage defaultMessage="Archived" />
                </Tag>
              </>
            ) : (
              product.name
            )
          }
          id={id}
          type={getProductTypeTitle(intl, product.type)}
          typeIcon={getProductTypeIconName(product.type)}
          actions={
            <Popover
              placement="bottom-end"
              content={(close) => (
                <Menu>
                  <MenuDivider text="Produkt" />
                  <MenuItem
                    text={intl.formatMessage({
                      defaultMessage: 'Edit details',
                      description: 'Edit product details',
                    })}
                    icon="edit"
                    onClick={() => {
                      close();
                      setShowDetails(true);
                    }}
                  />
                  <MenuItem
                    text={intl.formatMessage({
                      defaultMessage: 'Duplicate',
                      description: 'Duplicate product menu item',
                    })}
                    icon="duplicate"
                    onClick={() => {
                      productStore
                        .duplicate(product)
                        .then((newProduct) => {
                          toaster.addToast({
                            intent: 'success',
                            message: intl.formatMessage({
                              defaultMessage: 'Product has been duplicated',
                            }),
                          });
                          history.push(`/products/${newProduct.id}`);
                        })
                        .catch(toaster.showDefaultError);
                      close();
                    }}
                  />
                  <MenuDivider />
                  {product.deleted_at ? (
                    <MenuItem
                      text={intl.formatMessage({
                        defaultMessage: 'Restore product',
                        description: 'Actions Popover - Restore product item',
                      })}
                      intent="warning"
                      icon="refresh"
                      onClick={() => {
                        close();
                        productStore
                          .deleteItem(product)
                          .then(() => {
                            history.replace('/products');
                            toaster.addToast({
                              message: intl.formatMessage({
                                defaultMessage:
                                  'Product was restored successfully',
                              }),
                            });
                          })
                          .catch((error) => {
                            toaster.showDefaultError(error);
                          });
                      }}
                    />
                  ) : (
                    <MenuItem
                      text={intl.formatMessage({
                        defaultMessage: 'Archive',
                        description: 'Actions Popover - Delete product item',
                      })}
                      intent="danger"
                      icon="trash"
                      onClick={() => {
                        close();
                        deleteAlert.show(async () => {
                          productStore
                            .deleteItem(product)
                            .then(() => {
                              history.replace('/products');
                            })
                            .catch(toaster.showDefaultError);
                        });
                      }}
                    />
                  )}
                </Menu>
              )}
            >
              <Button iconRight="chevron-down">
                <FormattedMessage defaultMessage="Actions" />
              </Button>
            </Popover>
          }
        />

        <Section
          title="Detaljer"
          actions={
            <Button
              minimal
              iconRight="edit"
              onClick={() => setShowDetails(true)}
            >
              <FormattedMessage
                defaultMessage="Edit"
                description="Edit product"
              />
            </Button>
          }
        >
          <Box display="flex">
            <Box width="50%">
              <KeyValue keyName="Navn" value={product.name} />
              <KeyValue keyName="SKU" mono value={product.sku} />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Manufacturer number',
                  description: 'The manufacturers number',
                })}
                mono
                value={product.manufacturer_number}
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Retail price',
                  description: 'Product retail price',
                })}
                value={
                  <FormattedNumber
                    currency={product.retail_price_currency}
                    value={product.retail_price}
                    // eslint-disable-next-line react/style-prop-object
                    format="currency"
                  />
                }
                mono
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Cost price',
                  description: 'Product cost price',
                })}
                value={<CostPriceText costPrice={product.cost_price} />}
                mono
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Unit',
                  description: 'Product unit of measure',
                })}
                value={<FormattedUnit unit={product.unit} unitDisplay="long" />}
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Contribution margin',
                  description: 'Contribution margin on product',
                })}
                value={
                  <>
                    <ContributionMarginText
                      contributionMargin={product.contribution_margin}
                      showIndication
                    />{' '}
                    <span className="muted">
                      (
                      {product.contribution_margin_ratio.precise === null &&
                        '>'}
                      <FormattedNumber
                        // eslint-disable-next-line react/style-prop-object
                        style="percent"
                        value={
                          product.contribution_margin_ratio.precise ||
                          product.contribution_margin_ratio.minimum ||
                          0
                        }
                      />
                      )
                    </span>
                  </>
                }
              />
            </Box>
            <Box
              width="50%"
              paddingLeft="16px"
              borderLeft={`2px solid ${defaultColors.gray[100]}`}
              alignSelf="flex-start"
            >
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Product number',
                  description: 'Product number (identification)',
                })}
                value={id}
                mono
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Product group',
                })}
                value={
                  product.group === null ? undefined : product.group ? (
                    <Link
                      className="a"
                      to={`/product-groups/${product.group.id}`}
                    >
                      {product.group.name}
                    </Link>
                  ) : (
                    <Spinner size={16} />
                  )
                }
                mono
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Tariff number',
                  description: 'Product tariff number or HS code',
                })}
                value={product.tariff_number}
                mono
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Reference',
                  description: 'Product reference',
                })}
                mono
                value={product.reference}
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Description',
                  description: 'Product description',
                })}
                value={product.description}
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Tags',
                  description: 'Product tags',
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: 'No tags',
                  description: 'When product has no tags',
                })}
                value={
                  product.tags.length > 0
                    ? product.tags.map((t) => (
                        <Tag
                          key={t}
                          small={true}
                          marginRight={minorScale(1)}
                          intent="info"
                        >
                          {t}
                        </Tag>
                      ))
                    : undefined
                }
              />
            </Box>
          </Box>
        </Section>

        {product.type === ProductType.SERVICE && (
          <TaskManagers product={product} />
        )}

        {product.type === ProductType.GOOD && <StockStatus product={product} />}

        {product.type === ProductType.GOOD && <GoodTable product={product} />}

        {product.type === ProductType.PACKAGE && (
          <PackageTable product={product} />
        )}

        <ProductStatistics product={product} />
      </Container>
    </PageContainer>
  );
});

const TaskManagers = observer(({ product }: { product: IProduct }) => {
  const { productStore } = useStores();
  const intl = useIntl();
  const [saving, setSaving] = useState<ITeamMember | null>(null);

  return (
    <Section
      title={intl.formatMessage({
        defaultMessage: 'Possible assignments',
        description:
          'Describes the team members one can assign to a product (service)',
      })}
      actions={
        <UserSelect
          disabled={!!saving}
          onSelect={(user) => {
            const snapshot = getSnapshot(product);
            // If already added, we should just return.
            if (snapshot.task_managers.indexOf(user.id) > -1) return;
            setSaving(user);
            productStore
              .updateItem(product, {
                task_managers: [...snapshot.task_managers, user.id],
              })
              .finally(() => setSaving(null));
          }}
        >
          <Button minimal iconRight="add" disabled={!!saving}>
            <FormattedMessage defaultMessage="Add user" />
          </Button>
        </UserSelect>
      }
    >
      {product.task_managers.length === 0 && (
        <Box is="p" className="muted">
          <FormattedMessage defaultMessage="No assignments yet" />
        </Box>
      )}
      {product.task_managers.length > 0 && (
        <Table marginTop={majorScale(-2)}>
          <tbody>
            {product.task_managers.map((member, i) =>
              member ? (
                <tr key={(member as ITeamMember).id}>
                  <td>
                    <UserThumbnail
                      user={(member as ITeamMember).user}
                      showName
                    />
                  </td>
                  <td>
                    <Button
                      iconLeft="trash"
                      minimal
                      disabled={!!saving && saving !== member}
                      isLoading={!!saving && saving === member}
                      marginLeft="auto"
                      onClick={() => {
                        const { task_managers: taskManagers } =
                          getSnapshot(product);
                        setSaving(member);
                        productStore
                          .updateItem(product, {
                            task_managers: taskManagers.filter(
                              (uId) => uId !== member.id
                            ),
                          })
                          .finally(() => setSaving(null));
                      }}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={i} className="skeleton">
                  <td colSpan={2} />
                </tr>
              )
            )}
          </tbody>
        </Table>
      )}
    </Section>
  );
});

const StockStatus = observer(({ product }: { product: IProduct }) => {
  const [edit, setEdit] = useState(false);
  const [moveStock, setMoveStock] = useState(false);
  const intl = useIntl();
  const { productStore } = useStores();

  const [stockLocations, setStockLocations] =
    useState<IProductStockLocation[]>();

  useEffect(() => {
    let isSub = true;
    productStore
      .getProductLocations(product)
      .then((data: any) => isSub && setStockLocations(data.stock_locations));
    return () => {
      isSub = false;
    };
  }, [product, productStore]);

  const stockNotTrackedMessage = intl.formatMessage({
    defaultMessage: 'Stock not tracked',
  });

  return (
    <Section
      title="Lagerstatus"
      actions={
        <>
          <Popover
            placement="bottom-end"
            content={(close) => (
              <Menu>
                <MenuItem
                  icon="cube-add"
                  text="Update stock"
                  onClick={() => {
                    close();
                    setEdit(true);
                  }}
                />
                <MenuItem
                  icon="right-join"
                  text="Move stock"
                  onClick={() => {
                    close();
                    setMoveStock(true);
                  }}
                />
              </Menu>
            )}
          >
            <Button iconRight="chevron-down">
              <FormattedMessage defaultMessage="Stock actions" />
            </Button>
          </Popover>
        </>
      }
    >
      <StockDialog
        isShown={edit}
        onClose={() => setEdit(false)}
        product={product}
      />
      <MoveStockDialog
        isShown={moveStock}
        onClose={() => setMoveStock(false)}
        product={product}
      />
      <Box display="flex">
        <Box width="50%">
          <KeyValue
            keyName={intl.formatMessage({ defaultMessage: 'Own warehouse' })}
            placeholder={stockNotTrackedMessage}
            value={
              product.stock === null ? undefined : (
                <Box display="flex" alignItems="center">
                  <FormattedUnit unit={product.unit} value={product.stock} />
                  {stockLocations && stockLocations.length > 0 && (
                    <Popover
                      placement="right"
                      triggerOnHover
                      content={(close) => (
                        <Menu padding={majorScale(2)}>
                          <Heading
                            size={100}
                            className="muted"
                            marginBottom={majorScale(1)}
                          >
                            <FormattedMessage defaultMessage="Locations" />
                          </Heading>
                          {stockLocations.map((loc) => (
                            <Box
                              key={loc.location}
                              display="flex"
                              justifyContent="space-between"
                              marginTop={majorScale(0.5)}
                            >
                              <p>{loc.location_code}</p>
                              <Box is="p" paddingLeft={majorScale(2)}>
                                <FormattedUnit
                                  unit={product.unit}
                                  value={loc.stock}
                                />
                              </Box>
                            </Box>
                          ))}
                        </Menu>
                      )}
                    >
                      <Icon
                        icon="info-sign"
                        display="inline-block"
                        marginLeft={majorScale(1)}
                      />
                    </Popover>
                  )}
                </Box>
              )
            }
          />
          <KeyValue
            keyName={intl.formatMessage({ defaultMessage: 'Available' })}
            placeholder={stockNotTrackedMessage}
            value={
              product.available_stock === null ? undefined : (
                <>
                  <FormattedUnit
                    unit={product.unit}
                    value={product.available_stock}
                  />
                </>
              )
            }
          />
          <KeyValue
            keyName={intl.formatMessage({ defaultMessage: 'Reserved' })}
            placeholder={stockNotTrackedMessage}
            value={
              product.reserved_stock === null ? undefined : (
                <>
                  <FormattedUnit
                    unit={product.unit}
                    value={product.reserved_stock}
                  />
                </>
              )
            }
          />
        </Box>
        <Box
          width="50%"
          paddingLeft="16px"
          borderLeft={`2px solid ${defaultColors.gray[100]}`}
          alignSelf="flex-start"
        >
          <KeyValue
            keyName={intl.formatMessage({
              defaultMessage: 'Internal cost price',
            })}
            value={
              product.own_cost_price !== null ? (
                <FormattedNumber
                  value={product.own_cost_price}
                  format="currency"
                  currency={product.own_cost_price_currency!}
                />
              ) : undefined
            }
          />
          <KeyValue
            keyName={intl.formatMessage({ defaultMessage: 'Remote stock' })}
            mono
            value={
              <>
                <FormattedUnit
                  unit={product.unit}
                  value={product.shadow_stock || 0}
                  unitDisplay="long"
                />
              </>
            }
          />
        </Box>
      </Box>
    </Section>
  );
});

const GoodTable = observer(({ product }: { product: IProduct }) => {
  const intl = useIntl();
  const { supplierItemStore, productStore } = useStores();
  const [settingDefaultItem, setSettingDefaultItem] = useState<ISupplierItem>();
  const [editSupplierProductItem, setEditSupplierProductItem] =
    useState<ISupplierItem>();
  const [showSupplierProductDialog, setShowSupplierProductDialog] =
    useState(false);

  const removeSupplierItemAlert = useAlert({
    title: intl.formatMessage({
      defaultMessage: 'Removing supplier from product',
      description: 'Remove supplier alert title',
    }),
    message: intl.formatMessage({
      defaultMessage:
        'Are you sure you want to remove the supplier? This cannot be undone.',
    }),
    confirmButtonText: intl.formatMessage({
      id: 'defaults.remove',
      defaultMessage: 'Remove',
    }),
    intent: 'danger',
    icon: 'warning-sign',
  });

  const supplierItems = product.supplier_items.filter(
    (item) => item && !item.deleted_at
  );

  return (
    <Section title={intl.formatMessage({ defaultMessage: 'Suppliers' })}>
      <SupplierProductDialog
        isShown={showSupplierProductDialog}
        onClose={() => setShowSupplierProductDialog(false)}
        product={product}
      />
      <SupplierProductDialog
        isShown={!!editSupplierProductItem}
        onClose={() => setEditSupplierProductItem(undefined)}
        product={product}
        supplierProductItem={editSupplierProductItem}
      />
      {supplierItems.length > 0 && (
        <Table marginTop={majorScale(-2)} marginBottom={majorScale(1)}>
          <thead>
            <tr>
              <th>
                <FormattedMessage defaultMessage="Supplier" />
              </th>
              <th>
                <FormattedMessage defaultMessage="Supplier item number" />
              </th>
              <th className="right" style={{ width: '160px' }}>
                <FormattedMessage defaultMessage="In stock" />
              </th>
              <th className="right">
                <FormattedMessage defaultMessage="Cost price" />
              </th>
              <th className="right">
                <FormattedMessage
                  defaultMessage="Default"
                  description="Default supplier item"
                />
              </th>
              <th style={{ width: '40px' }}></th>
            </tr>
          </thead>
          <tbody>
            {supplierItems.map((supplierItem, i) =>
              supplierItem ? (
                <tr key={i}>
                  <td>
                    {supplierItem.supplier ? (
                      <CompanyThumbnail company={supplierItem.supplier} />
                    ) : (
                      <Spinner size={16} />
                    )}
                  </td>
                  <td className="font-mono">{supplierItem.supplier_number}</td>
                  <td className="right font-mono">
                    <FormattedUnit
                      unit={product.unit}
                      value={supplierItem.stock}
                    />
                  </td>
                  <td className="right font-mono">
                    <FormattedNumber
                      format="currency"
                      currency={supplierItem.cost_price_currency}
                      value={supplierItem.cost_price}
                    />
                  </td>
                  <td className="right">
                    {/* TODO: Maybe change this line */}
                    {product.default_supplier_item &&
                      product.default_supplier_item.id === supplierItem.id && (
                        <Icon icon="tick" />
                      )}
                    {settingDefaultItem === supplierItem && (
                      <Spinner size={16} />
                    )}
                  </td>
                  <td>
                    <Popover
                      placement="bottom-end"
                      content={(close) => (
                        <Menu>
                          <MenuItem
                            text={intl.formatMessage({
                              defaultMessage: 'Edit',
                            })}
                            onClick={() => {
                              close();
                              setEditSupplierProductItem(supplierItem);
                            }}
                          />
                          <MenuItem
                            text={intl.formatMessage({
                              defaultMessage: 'Set as default',
                            })}
                            disabled={!!settingDefaultItem}
                            onClick={async () => {
                              setSettingDefaultItem(supplierItem);
                              close();
                              try {
                                await product.setDefaultSupplierProductItem(
                                  supplierItem
                                );
                              } catch {
                                // Handle error
                              }
                              setSettingDefaultItem(undefined);
                            }}
                          />
                          <MenuDivider />
                          <MenuItem
                            text={intl.formatMessage({
                              defaultMessage: 'Remove',
                            })}
                            intent="danger"
                            onClick={() => {
                              close();
                              removeSupplierItemAlert.show(() =>
                                supplierItemStore
                                  .deleteItem(supplierItem)
                                  .then(() =>
                                    productStore.getItem(product.id, true)
                                  )
                              );
                            }}
                          />
                        </Menu>
                      )}
                    >
                      <Button minimal={true} iconLeft="more" />
                    </Popover>
                  </td>
                </tr>
              ) : (
                <tr key={i}>
                  <td colSpan={100}>
                    <Box className="skeleton" height="32px"></Box>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </Table>
      )}
      <Button iconLeft="add" onClick={() => setShowSupplierProductDialog(true)}>
        <FormattedMessage defaultMessage="Add supplier" />
      </Button>
    </Section>
  );
});

const PackageTable = observer(({ product }: { product: IProduct }) => {
  const intl = useIntl();
  const { bomItemStore, productStore } = useStores();

  const removeSupplierItemAlert = useAlert({
    title: intl.formatMessage({
      defaultMessage: 'Removing bom item',
      description: 'Remove suppler item alert title',
    }),
    message: intl.formatMessage({
      defaultMessage:
        'Are you sure you want to remove the bom item? This cannot be undone.',
    }),
    confirmButtonText: intl.formatMessage({
      id: 'defaults.remove',
      defaultMessage: 'Remove',
    }),
    intent: 'danger',
    icon: 'warning-sign',
  });

  const [editBOM, setEditBOM] = useState<IBOMItem>();
  const [addBOM, setAddBOM] = useState(false);
  const [createProduct, setCreateProduct] = useState(false);

  return (
    <Section
      title={intl.formatMessage({
        defaultMessage: 'Bill of materials',
        description: 'A section title',
      })}
    >
      <BOMDialog
        isShown={addBOM}
        onClose={() => setAddBOM(false)}
        product={product}
      />
      <BOMDialog
        isShown={!!editBOM}
        onClose={() => setEditBOM(undefined)}
        product={product}
        bomItem={editBOM}
      />
      <ProductDetailsDialog
        isShown={createProduct}
        onClose={async (newProduct) => {
          setCreateProduct(false);
          if (newProduct) {
            await bomItemStore.createItem({
              parent: product.id,
              quantity: 1,
              product: newProduct.id,
            });
            await productStore.getItem(product.id, true);
          }
        }}
      />
      {product.bill_of_materials.length > 0 && (
        <Table marginTop={majorScale(-2)} marginBottom={majorScale(1)}>
          <thead>
            <tr>
              <th>
                <FormattedMessage defaultMessage="Product" />
              </th>
              <th>
                <FormattedMessage defaultMessage="Supplier" />
              </th>
              <th className="right">
                <FormattedMessage defaultMessage="Quantity" />
              </th>
              <th className="right">
                <FormattedMessage defaultMessage="Cost price" />
              </th>
              <th style={{ width: '40px' }}></th>
            </tr>
          </thead>
          <tbody>
            {product.bill_of_materials.map((bomItem, i) =>
              bomItem ? (
                <tr key={bomItem.id}>
                  <td>
                    {bomItem.product ? (
                      <ProductThumbnail
                        maxWidth="400px"
                        showSKU
                        product={bomItem.product}
                        showName
                      />
                    ) : (
                      <Spinner size={16} />
                    )}
                  </td>
                  <td>
                    {bomItem.default_supplier_item &&
                    bomItem.default_supplier_item.supplier ? (
                      <CompanyThumbnail
                        company={
                          bomItem.default_supplier_item.supplier as ICompany
                        }
                      />
                    ) : bomItem.default_supplier_item === undefined ||
                      (bomItem.default_supplier_item &&
                        !bomItem.default_supplier_item.supplier) ? (
                      <Spinner size={16} />
                    ) : (
                      <FormattedMessage defaultMessage="None specified" />
                    )}
                  </td>
                  <td className="right font-mono">
                    {bomItem.product ? (
                      <FormattedUnit
                        value={bomItem.quantity}
                        unit={bomItem.product.unit}
                      />
                    ) : (
                      <Spinner size={16} />
                    )}
                  </td>
                  <td className="right font-mono">
                    <CostPriceText costPrice={bomItem.cost_price} />
                  </td>
                  <td>
                    <Popover
                      placement="bottom-end"
                      content={(close) => (
                        <Menu>
                          <MenuItem
                            text={intl.formatMessage({
                              defaultMessage: 'Edit',
                            })}
                            onClick={() => {
                              close();
                              setEditBOM(bomItem);
                            }}
                          />
                          <MenuDivider />
                          <MenuItem
                            text={intl.formatMessage({
                              defaultMessage: 'Remove',
                            })}
                            intent="danger"
                            onClick={() => {
                              close();
                              removeSupplierItemAlert.show(() =>
                                bomItemStore
                                  .deleteItem(bomItem)
                                  .then(() =>
                                    productStore.getItem(product.id, true)
                                  )
                              );
                            }}
                          />
                        </Menu>
                      )}
                    >
                      <Button minimal={true} iconLeft="more" />
                    </Popover>
                  </td>
                </tr>
              ) : (
                <tr key={'index-' + i}>
                  <td colSpan={100}>
                    <Box className="skeleton" height="32px"></Box>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </Table>
      )}
      <Box display="flex">
        <Button
          iconLeft="link"
          onClick={() => setAddBOM(true)}
          marginRight={majorScale(1)}
        >
          <FormattedMessage defaultMessage="Add product" />
        </Button>
        <Button iconLeft="add" onClick={() => setCreateProduct(true)}>
          <FormattedMessage defaultMessage="Create new product" />
        </Button>
      </Box>
    </Section>
  );
});
